/** Inventory page list: positive On Hand by default, optional zero-stock rows. */

export const INVENTORY_LIST_STATUSES = ['Received', 'Stored', 'Processing', 'Packaged'] as const;

export const SHOW_ZERO_STOCK_KEY = 'tilly.inventory.showZeroStock';

export function readShowZeroStock(): boolean {
  try {
    return localStorage.getItem(SHOW_ZERO_STOCK_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeShowZeroStock(on: boolean): void {
  try {
    localStorage.setItem(SHOW_ZERO_STOCK_KEY, on ? '1' : '0');
  } catch {
    /* private mode / blocked storage */
  }
}

export function hasPositiveOnHand(item: { quantity?: string | number | null }): boolean {
  const n = Number(item?.quantity);
  return Number.isFinite(n) && n > 0;
}

export interface InventoryRecordRef {
  identifier?: string | null;
  siteId?: string | null;
  locationId?: number | string | null;
  account?: string | null;
  lotNumber?: string | null;
  locationName?: string | null;
}

/** React key. Same item name at Madison and Athens must not share a key. */
export function inventoryRowKey(item: InventoryRecordRef): string {
  return `${item.identifier || ''}|${item.siteId || ''}|${item.locationId ?? ''}|${item.account || ''}`;
}

/** Query string so item detail opens this On Hand row, not the first name match. */
export function inventoryRecordSearch(item: InventoryRecordRef): string {
  const qs = new URLSearchParams();
  if (item.siteId) qs.set('siteId', item.siteId);
  if (item.locationId != null && String(item.locationId) !== '') {
    qs.set('locationId', String(item.locationId));
  }
  qs.set('account', item.account || '');
  return qs.toString();
}

export function pickInventoryRecord<T extends InventoryRecordRef>(
  items: T[],
  query: {
    identifier: string;
    siteId?: string;
    locationId?: string;
    account?: string;
    hasAccount?: boolean;
  }
): T | null {
  const named = (items || []).filter((item) => (item.identifier || '') === query.identifier);
  if (!named.length) return null;
  const wantsRow = !!(query.siteId || query.locationId || query.hasAccount);
  if (!wantsRow) return named[0];
  return (
    named.find((item) => {
      if (query.siteId && (item.siteId || '') !== query.siteId) return false;
      if (query.locationId && String(item.locationId ?? '') !== query.locationId) return false;
      if (query.hasAccount && (item.account || '') !== (query.account || '')) return false;
      return true;
    }) || null
  );
}

/**
 * Every word must appear in the item name, lot, or location.
 * Type and description are ignored — those fields tie ingredients to unrelated products.
 */
export function matchesInventorySearch(
  item: InventoryRecordRef,
  searchTerm: string,
  locationName?: string | null
): boolean {
  const words = searchTerm
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return true;
  const haystack = [item.identifier, item.lotNumber, locationName ?? item.locationName]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean)
    .join(' ');
  return words.every((word) => haystack.includes(word));
}

export function filterInventoryList<
  T extends InventoryRecordRef & {
    type?: string;
    description?: string;
    status?: string;
    quantity?: string | number | null;
  },
>(
  rows: T[],
  searchTerm: string,
  showZeroStock: boolean,
  locationNameFor?: (item: T) => string | undefined
): T[] {
  return (rows || []).filter((item) => {
    if (!item?.status || !INVENTORY_LIST_STATUSES.includes(item.status as (typeof INVENTORY_LIST_STATUSES)[number])) {
      return false;
    }
    if (!showZeroStock && !hasPositiveOnHand(item)) return false;
    return matchesInventorySearch(item, searchTerm, locationNameFor?.(item));
  });
}
